/*
 * Generic Core <-> Enterprise Edition bridge.
 *
 * Core code (src/Common, src/Screens, src/store outside of src/ee) must never import
 * from src/ee directly -- src/ee only exists on the professional branch. Instead, Core
 * exposes a "slot" identified by a featureKey, and src/ee/register.js is the only file
 * that registers the real implementation into that slot when the app boots.
 *
 * On community/dev, src/ee/register.js does not exist, nothing ever registers, and every
 * lookup below returns null/undefined -- which is exactly what EEFeatureSlot (components)
 * and getEEAction/getEESelector (thunks/selectors) treat as "this feature isn't here".
 *
 * Active featureKeys (kept in one place so a Pro-only fallback decision is easy to audit):
 * - quick-planner, dashboard-analytics-widget, notifications-dropdown, workspace-multi-user,
 *   cloud-agent-detail, settings-report-tab, settings-notifications-tab, user-role-dropdown,
 *   risk-score-filter, tasks-notification-action, filters-pro-entities, workspace-detail-users,
 *   workspace-public-toggle, workspace-group-by-control
 */
const registry = new Map();
const actionRegistry = new Map();
const selectorRegistry = new Map();
const listRegistry = new Map();

export function registerEEFeature (key, Component) {
  registry.set(key, Component);
}

export function getEEFeature (key) {
  return registry.get(key) || null;
}

export function registerEEAction (key, action) {
  actionRegistry.set(key, action);
}

export function getEEAction (key) {
  return actionRegistry.get(key) || null;
}

export function registerEESelector (key, selector) {
  selectorRegistry.set(key, selector);
}

export function getEESelector (key, fallback) {
  return selectorRegistry.get(key) || fallback;
}

/*
 * For config lists a Core file builds piece-by-piece (sidebar sections, search
 * entities, etc) where some entries only make sense when EE features exist, and need
 * per-render context (e.g. permission checks against the current state) rather than a
 * static value. registerEEList takes a builder function; Core calls getEEList(key, ...args)
 * and splices the result into its own array -- it's [] until ee/register.js registers
 * the real builder.
 */
export function registerEEList (key, builder) {
  listRegistry.set(key, builder);
}

export function getEEList (key, ...args) {
  const builder = listRegistry.get(key);
  return builder ? builder(...args) : [];
}
